import { Anchor, Badge, Button, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconLink, IconRefresh } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { GroupChildrenRepository, GroupSearchRepository } from "shared/components/organization/groups/data";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { GroupLikeFragment } from "src/gql/graphql";
import { container } from "tsyringe";

const TableRow = ({ type, subject, id }: { type: "group" | "sender"; subject: string; id: string }) => {
    return (
        <Table.Tr>
            <Table.Td>{type == "group" ? <Badge color="red">Group</Badge> : <Badge color="blue">Sender</Badge>}</Table.Td>
            <Table.Td>
                <Text fz="sm">{subject}</Text>
            </Table.Td>
            <Table.Td>
                <Anchor c="var(--mantine-color-text)" target="_blank" href={`/organization/${type}s/${id}`} aria-label="group link">
                    <IconLink size={16} />
                </Anchor>
            </Table.Td>
        </Table.Tr>
    );
};

export function useArchiveGroup(group: GroupLikeFragment) {
    const nav = useNavigate();

    const {
        data: {
            group: { blockers },
        },
    } = useSuspenseQuery({
        queryKey: qk("group", "id", group.id, "blockers"),
        queryFn: () => httpPostGraphql(gql.blockers, { id: group.id }),
    });

    const { mutateAsync } = useGraphqlMutation({
        document: gql.archiveGroup,
        onSuccess: async () => {
            await qk
                .invalidate("group")
                .then(() => nav({ to: "/organization/groups" }).then(() => notify.show.success({ message: "Group archived" })));
        },
    });

    const Modal = () => {
        const {
            data: {
                group: { blockers },
            },
            isRefetching,
        } = useSuspenseQuery({
            queryKey: qk("group", "id", group.id, "blockers"),
            queryFn: () => httpPostGraphql(gql.blockers, { id: group.id }),
        });

        const rows = blockers.groups
            .map((it) => <TableRow type="group" subject={it.name} id={it.id} key={it.id} />)
            .concat(blockers.senders.map((it) => <TableRow type="sender" subject={it.name} id={it.id} key={it.id} />));
        return (
            <Stack>
                <Text>Groups cannot be archived until their senders and sub-groups are deleted</Text>
                <Table striped>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Item</Table.Th>
                            <Table.Th>Value</Table.Th>
                            <Table.Th w={1}>
                                <Button
                                    variant="default"
                                    rightSection={isRefetching ? <Loader color="white" size="sm" /> : <IconRefresh />}
                                    onClick={() => {
                                        qk.invalidate("group", "id", group.id, "blockers").catch(notify.catch);
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{rows}</Table.Tbody>
                </Table>
                {!rows.length && (
                    <Group justify="center">
                        <Text fs="italic">None</Text>
                    </Group>
                )}
                <Group w="100%" justify="flex-end">
                    <Button
                        disabled={blockers.isBlocked}
                        color="red"
                        onClick={() => void mutateAsync({ groupId: group.id }).then(modals.closeAll).catch(notify.catch)}
                    >
                        Archive
                    </Button>
                </Group>
            </Stack>
        );
    };

    async function archiveGroup() {
        const groupChildrenRepo = container.resolve(GroupChildrenRepository);
        const groupSearchRepo = container.resolve(GroupSearchRepository);

        if (blockers.isBlocked)
            modals.open({
                title: "Archive Group",
                children: (
                    <Suspense fallback={<Loader />}>
                        <Modal />
                    </Suspense>
                ),
                onClose: modals.closeAll,
                size: "lg",
            });
        else {
            await notify.confirm("Are you sure you want to delete this group?", {
                title: "Confirm",
            });
            mutateAsync({ groupId: group.id })
                .then(() => {
                    groupChildrenRepo.remove(group.id);
                    groupSearchRepo.remove(group.id);
                })
                .finally(() => modals.closeAll())
                .catch(notify.catch);
        }
    }

    return {
        archiveGroup,
    };
}

const gql = {
    blockers: graphql(`
        query UseArchiveGroupBlockers($id: UUID!) {
            group(id: $id) {
                blockers {
                    isBlocked
                    groups {
                        id
                        name
                    }
                    senders {
                        id
                        name
                        group {
                            id
                        }
                    }
                }
            }
        }
    `),
    archiveGroup: graphql(`
        mutation UseArchiveGroup($groupId: UUID!) {
            archiveGroup(id: $groupId) {
                blockers {
                    isBlocked
                }
            }
        }
    `),
};
